import { useEffect, type RefObject } from "react";

// A single shared window listener feeds all tracked elements, rather than one listener per hook instance.
const mouse = { x: 0, y: 0 };
let mouseListenerCount = 0;
function onMouseMove(e: MouseEvent) {
  mouse.x = e.clientX;
  mouse.y = e.clientY;
}

export function useMouseTracking<T extends HTMLElement>(
  ref: RefObject<T | null>,
  maxOffset: number
) {
  useEffect(() => {
    if (mouseListenerCount === 0) {
      window.addEventListener("mousemove", onMouseMove);
    }
    mouseListenerCount++;
    return () => {
      mouseListenerCount--;
      if (mouseListenerCount === 0) {
        window.removeEventListener("mousemove", onMouseMove);
      }
    };
  }, []);

  useEffect(() => {
    // 0 disables tracking, freeing the element's transform for a CSS animation
    // (e.g. the sleeping avatar's breathing) to own.
    if (maxOffset === 0) return;
    let raf: number;
    function tick() {
      const el = ref.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = mouse.x - cx;
        const dy = mouse.y - cy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const strength = Math.min(dist, 200) / 200;
        const ox = dist > 0 ? (dx / dist) * strength * maxOffset : 0;
        const oy = dist > 0 ? (dy / dist) * strength * maxOffset : 0;
        el.style.transform = `translate(${ox}px, ${oy}px)`;
      }
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [ref, maxOffset]);
}
